/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  encodeBase64,
  decodeBase64,
  getItemFromLocalStorage,
} from 'lib/helper';
import moment from 'moment';
import { SourceType } from 'views/Findings/state';

import {
  DashboardType,
  IntervalFilter,
  IntervalFilterType,
  Severity,
  TimeBandView,
  TimespanFilter,
  VulnStatus,
  VulnerabilityStatus,
} from './types';

export function applyTimespanFilter(
  historicalDataArray: any[],
  timespanFilter: TimespanFilter,
): [] {
  let resultArray;
  const arrLength = historicalDataArray?.length;

  switch (timespanFilter) {
    case TimespanFilter.OneMonth:
      resultArray = historicalDataArray.slice(Math.max(arrLength - 4, 0));
      break;
    case TimespanFilter.ThreeMonth:
      resultArray = historicalDataArray.slice(Math.max(arrLength - 13, 0));
      break;
    case TimespanFilter.SixMonth:
      resultArray = historicalDataArray.slice(Math.max(arrLength - 26, 0));
      break;
    case TimespanFilter.OneYear:
      resultArray = historicalDataArray.slice(Math.max(arrLength - 52, 0));
      break;
    default:
      throw Error(
        `applyTimespanFilter() function is missing the following case: ${timespanFilter}`,
      );
  }

  return resultArray;
}

export function applyIntervalFilter(
  historicalDataArray: any[],
  intervalFilter: IntervalFilter,
  intervalFilterType: IntervalFilterType,
): any[] {
  let numberOfNodes = 0;
  switch (intervalFilter) {
    case IntervalFilter.Week:
      numberOfNodes = 0;
      break;
    case IntervalFilter.FourWeeks:
      numberOfNodes = 3;
      break;
    case IntervalFilter.ThreeMonth:
      numberOfNodes = 12;
      break;
    case IntervalFilter.SixMonth:
      numberOfNodes = 25;
      break;
    default:
      throw Error(
        `applyIntervalFilter() function is missing the following case: ${intervalFilter}`,
      );
  }

  if (numberOfNodes > 0)
    return intervalFilterType === IntervalFilterType.SkipRecords
      ? filterOutArrayNodes(historicalDataArray, numberOfNodes)
      : mergeArrayNodes(historicalDataArray, numberOfNodes);

  return historicalDataArray;
}

function filterOutArrayNodes(historicalDataArray: any[], numberOfNodes): any[] {
  const resultArray: any[] = [];
  const arrLength = historicalDataArray?.length;
  let nodeCounter = numberOfNodes;
  // reverse traverse the array
  for (let i = arrLength - 1; i >= 0; i--) {
    if (i === arrLength - 1) {
      resultArray.push(historicalDataArray[i]);
      continue;
    } else if (nodeCounter === 0) {
      resultArray.push(historicalDataArray[i]);
      nodeCounter = numberOfNodes;
    } else nodeCounter--;
  }

  return resultArray.reverse();
}

function mergeArrayNodes(historicalDataArray: any[], numberOfNodes): any[] {
  const arrLength = historicalDataArray?.length;
  if (numberOfNodes === 0) return historicalDataArray;

  const resultArray: any[] = [];

  for (let i = arrLength - 1; i >= 0; i -= numberOfNodes + 1) {
    const startIndex = i;
    let endIndex = i - numberOfNodes;
    if (endIndex < 0) endIndex = 0;
    let total = 0;
    for (let j = startIndex; j >= endIndex; j--) {
      total += historicalDataArray[j];
    }

    resultArray.push(total);
  }

  return resultArray.reverse();
}

export function mergeArrays(dataArrays: number[][]) {
  const resultArray: number[] = [];
  for (let i = 0; i < dataArrays.length; i++) {
    for (let j = 0; j < dataArrays[i].length; j++) {
      const dataArray = dataArrays[i];
      if (resultArray[j]) resultArray[j] += dataArray[j];
      else resultArray[j] = dataArray[j];
    }
  }
  return resultArray;
}

export function avgArrays(dataArrays: number[][], countDataArrays: any) {
  const resultArray: number[] = [];
  // calculates the number of non zero elements at a specific index
  const divisorArray: number[] = [];
  for (let i = 0; i < dataArrays.length; i++) {
    for (let j = 0; j < dataArrays[i].length; j++) {
      const dataArray = dataArrays[i];
      const countArray = countDataArrays[i];
      if (resultArray[j]) {
        resultArray[j] += dataArray[j] * countArray[j];
        divisorArray[j] += countArray[j];
      } else {
        resultArray[j] = dataArray[j] * countArray[j];
        divisorArray[j] = countArray[j];
      }
    }
  }

  for (let i = 0; i < resultArray.length; i++) {
    if (resultArray[i] !== 0 && divisorArray[i] !== 0) {
      resultArray[i] = parseFloat(
        (resultArray[i] / divisorArray[i]).toFixed(1),
      );
    } else {
      resultArray[i] = 0;
    }
  }
  return resultArray;
}

export function checkNonZeroArray(array: number[]): boolean {
  return array?.filter((x) => x > 0).length > 0;
}

export function checkNonZeroObj(obj) {
  for (const key in obj) {
    if (
      Object.prototype.hasOwnProperty.call(obj, key) &&
      typeof obj[key] === 'object'
    ) {
      if (checkNonZeroObj(obj[key])) {
        return true;
      }
    } else {
      if (obj[key] !== 0) {
        return true;
      }
    }
  }
  return false;
}

interface DrillDownProps {
  type: string;
  severity: string;
  sourceType: string;
  issueType: string;
  qid: string;
  rule_id?: string;
  search?: string;
  severity_in;
  hosts_in;
  patchable?: boolean;
  obsolete?: boolean;
  created_from_date?;
  created_to_date?;
  reopened_from_date?;
  reopened_to_date?;
  fixed_from_date?;
  fixed_to_date?;
  ignored_from_date?;
  ignored_to_date?;
  age_min?;
  age_max?;
}

export function getDrillDownUrl(props: DrillDownProps) {
  return __getDrillDownUrlV2(props);
}

/**
 * DEPRECIATED: Should not be used
 * Drill down function used for old vuln listing page
 */
export function getDrillDownUrl_old({
  type,
  severity,
  sourceType,
  issueType,
  qid,
  rule_id = undefined,
  search,
  severity_in,
  hosts_in,
  patchable = false,
  obsolete = false,
  created_from_date = undefined,
  created_to_date = undefined,
  reopened_from_date = undefined,
  reopened_to_date = undefined,
  fixed_from_date = undefined,
  fixed_to_date = undefined,
  ignored_from_date = undefined,
  ignored_to_date = undefined,
  age_min = undefined,
  age_max = undefined,
}: DrillDownProps) {
  let url = `qualys/vulnerabilities?type=${type}`;

  if (severity) url = `${url}&severity=${severity}`;
  if (sourceType) url = `${url}&source_type=${sourceType}`;
  if (issueType) url = `${url}&issue_type=${issueType}`;
  if (qid) url = `${url}&qid=${qid}`;
  if (rule_id) url = `${url}&rule_id=${rule_id}`;
  if (search) url = `${url}&search=${search}`;
  if (patchable) url = `${url}&patchable=1`;
  if (obsolete) url = `${url}&obsolete=1`;

  if (created_from_date) url = `${url}&created_from_date=${created_from_date}`;
  if (created_to_date) url = `${url}&created_to_date=${created_to_date}`;
  if (reopened_from_date)
    url = `${url}&reopened_from_date=${reopened_from_date}`;
  if (reopened_to_date) url = `${url}&reopened_to_date=${reopened_to_date}`;
  if (fixed_from_date) url = `${url}&fixed_from_date=${fixed_from_date}`;
  if (fixed_to_date) url = `${url}&fixed_to_date=${fixed_to_date}`;
  if (ignored_from_date) url = `${url}&ignored_from_date=${ignored_from_date}`;
  if (ignored_to_date) url = `${url}&ignored_to_date=${ignored_to_date}`;

  if (severity_in) url = `${url}&severity_in=${severity_in}`;
  if (hosts_in) url = `${url}&hosts_in=${hosts_in}`;

  if (age_min != undefined) url = `${url}&age_min=${age_min}`;
  if (age_max != undefined) url = `${url}&age_max=${age_max}`;

  return url;
}

/**
 * NOTE: Should not be called directly, instead call getDrillDownUrl()
 * Internal Drill down function used for new vuln listing page
 * @param param0
 * @returns
 */
export function __getDrillDownUrlV2({
  type,
  severity,
  sourceType,
  issueType,
  qid,
  rule_id = undefined,
  search,
  severity_in,
  hosts_in,
  patchable = false,
  obsolete = false,
  created_from_date = undefined,
  created_to_date = undefined,
  reopened_from_date = undefined,
  reopened_to_date = undefined,
  fixed_from_date = undefined,
  fixed_to_date = undefined,
  ignored_from_date = undefined,
  ignored_to_date = undefined,
  age_min = undefined,
  age_max = undefined,
}: DrillDownProps) {
  // translate status
  let statusValue: string = '';
  switch (type) {
    case 'all':
      statusValue = '';
      break;
    case 'open':
      statusValue = 'NEW,ACTIVE,REOPENED';
      break;
    case 'new':
      statusValue = 'NEW';
      break;
    case 'active':
      statusValue = 'ACTIVE';
      break;
    case 'reopened':
      statusValue = 'REOPENED';
      break;
    case 'closed':
      statusValue = 'FIXED,IGNORED';
      break;
    case 'fixed':
      statusValue = 'FIXED';
      break;
    case 'ignored':
      statusValue = 'IGNORED';
      break;
    default:
      statusValue = type;
  }

  // translate source type
  switch (sourceType) {
    case 'ca_data-all':
      sourceType = SourceType.CA;
      break;
    case 'ca_data':
      sourceType = SourceType.CA_SERVER;
      break;
    case 'ca_data-workstations':
      sourceType = SourceType.CA_WORKSTATIONS;
      break;
    case 'was_data':
      sourceType = SourceType.WAS;
      break;
    case 'endpoint_data':
      sourceType = SourceType.EP;
      break;
    case 'endpoint_data_internal':
      sourceType = SourceType.EP_INTERNAL;
      break;
    case 'endpoint_data_external':
      sourceType = SourceType.EP_EXTERNAL;
      break;
    case 'endpoint_data_ec2':
      sourceType = SourceType.EP_EC2;
      break;
    case 'sast2':
      sourceType = 'sast';
      break;
    default:
      break;
  }
  // translate issue type
  if (issueType == 'All') issueType = '';
  let url = `vulnerabilities?status=${statusValue}`;

  if (severity) url = `${url}&severity=${severity}`;
  if (sourceType) url = `${url}&source_type=${sourceType}`;
  // issue type only valid for sast
  if (sourceType === 'sast' && issueType)
    url = `${url}&issue_type=${issueType}`;
  if (qid) url = `${url}&qid=${qid}`;
  if (rule_id) url = `${url}&sonarqube_rule_id=${rule_id}`;
  if (search) url = `${url}&search=${search}`;
  if (patchable) url = `${url}&pre_filter__knowledge_base__patchable=1`;
  if (obsolete) url = `${url}&pre_filter__title__icontains=Obsolete`;

  if (created_from_date)
    url = `${url}&dt_filter__created_at__from=${created_from_date}`;
  if (created_to_date)
    url = `${url}&dt_filter__created_at__to=${created_to_date}`;
  if (reopened_from_date)
    url = `${url}&dt_filter__last_reopened_datetime__from=${reopened_from_date}`;
  if (reopened_to_date)
    url = `${url}&dt_filter__last_reopened_datetime__to=${reopened_to_date}`;
  if (fixed_from_date)
    url = `${url}&dt_filter__last_fixed_datetime__from=${fixed_from_date}`;
  if (fixed_to_date)
    url = `${url}&dt_filter__last_fixed_datetime__to=${fixed_to_date}`;
  if (ignored_from_date)
    url = `${url}&dt_filter__ignored_date__from=${ignored_from_date}`;
  if (ignored_to_date)
    url = `${url}&dt_filter__ignored_date__to=${ignored_to_date}`;

  if (severity_in) url = `${url}&severity=${severity_in}`;
  if (hosts_in?.length > 0) url = `${url}&host_ids=${hosts_in}`;

  if (age_min != undefined) url = `${url}&age__min_exclusive=${age_min}`;
  if (age_max != undefined) url = `${url}&age__max=${age_max}`;

  return url;
}

export function getHistoryDrillDownUrl(
  commonParameters,
  type,
  lastIntervalDate,
) {
  const startOfWeek = getStartOfWeekDateWithOffset();
  switch (type) {
    case 'all':
      return getDrillDownUrl(commonParameters);
    case 'open':
      return getDrillDownUrl(commonParameters);
    case 'new':
      return getDrillDownUrl({
        ...commonParameters,
        type: 'all',
        created_from_date: startOfWeek,
      });
    case 'reopened':
      return getDrillDownUrl({
        ...commonParameters,
        type: 'all',
        reopened_from_date: startOfWeek,
      });
    case 'fixed':
      return getDrillDownUrl({
        ...commonParameters,
        type: 'all',
        fixed_from_date: startOfWeek,
      });
    case 'ignored':
      return getDrillDownUrl({
        ...commonParameters,
        type: 'all',
        ignored_from_date: startOfWeek,
      });
    case 'lastIntervalNew':
      return getDrillDownUrl({
        ...commonParameters,
        type: 'all',
        created_from_date: lastIntervalDate,
      });
    case 'lastIntervalReopened':
      return getDrillDownUrl({
        ...commonParameters,
        type: 'all',
        reopened_from_date: lastIntervalDate,
      });
    case 'lastIntervalFixed':
      return getDrillDownUrl({
        ...commonParameters,
        type: 'all',
        fixed_from_date: lastIntervalDate,
      });
    case 'lastIntervalIgnored':
      return getDrillDownUrl({
        ...commonParameters,
        type: 'all',
        ignored_from_date: lastIntervalDate,
      });
    default:
      return '';
  }
}

export function getChartSubtitle(prefix: string, interval: IntervalFilter) {
  switch (interval) {
    case IntervalFilter.Week:
      return `${prefix} (Last week)`;
    case IntervalFilter.FourWeeks:
      return `${prefix} (Last 4 weeks)`;
    case IntervalFilter.ThreeMonth:
      return `${prefix} (Last 3 months)`;
    case IntervalFilter.SixMonth:
      return `${prefix} (Last 6 months)`;
    default:
      return prefix;
  }
}
export function getVulnerabilityStatusValue(
  status: VulnerabilityStatus,
): string {
  switch (status) {
    case VulnerabilityStatus.Active:
      return 'active';
    case VulnerabilityStatus.New:
      return 'new';
    case VulnerabilityStatus.Reopened:
      return 'reopened';
    case VulnerabilityStatus.Fixed:
      return 'fixed';
    case VulnerabilityStatus.Ignored:
      return 'ignored';
  }
}

export const getChartLegendColor = (label: string) => {
  switch (label) {
    case Severity.Urgent:
    case VulnerabilityStatus.Active:
    case TimeBandView.ZeroToThree:
      // return "rgba(207,46,46, 1)";
      return '#055193';
    case Severity.Critical:
    case VulnerabilityStatus.New:
    case TimeBandView.ThreeToSeven:
      // return "rgba(254,45,45, 1)";
      return '#146FBF';
    case Severity.Serious:
    case VulnerabilityStatus.Reopened:
    case TimeBandView.SevenToFourteen:
      // return "rgba(255,105,0,1)";
      return '#0096FF';
    case Severity.Medium:
    case TimeBandView.FourteenToTwentyEight:
      // return "rgba(252,185,0,1)";
      return '#5FCFFF';
    case Severity.Minimal:
    case VulnerabilityStatus.Fixed:
    case TimeBandView.TwentyEightPlus:
      // return "rgba(113,206,126, 1)";
      return '#C2EDFF';
    case VulnerabilityStatus.Ignored:
      return 'rgba(10, 161, 221, 1)';
    default:
      return 'rgba(128, 128, 128, 0.2)';
  }
};

export function getSeverityValue(severity: Severity): number {
  switch (severity) {
    case Severity.Urgent:
      return 5;
    case Severity.Critical:
      return 4;
    case Severity.Serious:
      return 3;
    case Severity.Medium:
      return 2;
    case Severity.Minimal:
      return 1;
  }
  return 0;
}

export function getTimeLabelValue(timebandLabel: string): any[] {
  switch (timebandLabel) {
    case TimeBandView.ZeroToThree:
      return [undefined, 3];
    case TimeBandView.ThreeToSeven:
      return [3, 7];
    case TimeBandView.SevenToFourteen:
      return [7, 14];
    case TimeBandView.FourteenToTwentyEight:
      return [14, 28];
    case TimeBandView.TwentyEightPlus:
      return [28, undefined];
  }
  return [0, 0];
}

export function getSeverityFilterValues(
  severityFilters: string[],
): number[] | undefined {
  if (severityFilters?.length === 5 || severityFilters?.length === 0)
    return undefined;

  return severityFilters.map((s) => getSeverityValue(Severity[s]));
}

export function getLastCompleteNodeDate(historyChartLabels, weeksYearMap) {
  let lastCompleteDateWeek = historyChartLabels[0];
  if (historyChartLabels.length > 2) {
    lastCompleteDateWeek = historyChartLabels[historyChartLabels.length - 2];
  }
  const currentYear = weeksYearMap[lastCompleteDateWeek];
  const fullDateString = lastCompleteDateWeek + ' ' + currentYear;
  const lastCompleteDate = new Date(fullDateString);
  lastCompleteDate.setDate(lastCompleteDate.getDate() + 1);
  const momentDate = moment(lastCompleteDate).valueOf();
  return momentDate;
}

export function getStartOfWeekDateWithOffset() {
  const dateWithOffset = moment(moment().startOf('week')).add(
    moment().utcOffset(),
    'minutes',
  );
  return dateWithOffset.valueOf();
}

export function getLastIntervalDate(intervalFilter: IntervalFilter) {
  const currentDate = moment(); // Get the current date and time in the current timezone

  // Start of the current day
  const startOfDay = currentDate.startOf('day');

  switch (intervalFilter) {
    case IntervalFilter.Week:
      return startOfDay.subtract(6, 'days').valueOf(); // Get the start of the day 1 week ago including today
    case IntervalFilter.FourWeeks:
      return startOfDay.subtract(27, 'days').valueOf(); // Get the start of the day 4 weeks ago including today
    case IntervalFilter.ThreeMonth:
      return startOfDay.subtract(89, 'days').valueOf(); // Get the start of the day 3 months ago including today
    case IntervalFilter.SixMonth:
      return startOfDay.subtract(179, 'days').valueOf(); // Get the start of the day 6 months ago including today
    default:
      return startOfDay.valueOf();
  }
}

export function getNewDataGenerationDate(dashboardType: DashboardType): {
  week: string;
  year: number;
} {
  switch (dashboardType) {
    case DashboardType.CA:
      return { week: 'Sep 03', year: 2022 };
    case DashboardType.EP:
      return { week: 'Oct 08', year: 2022 };
    case DashboardType.WAS:
      return { week: 'Oct 22', year: 2022 };
    case DashboardType.SAST:
      return { week: '', year: 2022 };
    case DashboardType.SAST2:
      return { week: '', year: 2022 };
    default:
      throw new Error(`Unhandled DashboardType: ${dashboardType}`);
  }
}

export function getAllIndexes(arr, val) {
  const indexes: number[] = [];
  let i = -1;
  while ((i = arr.indexOf(val, i + 1)) !== -1) {
    indexes.push(i);
  }
  return indexes;
}

export { encodeBase64, decodeBase64, getItemFromLocalStorage };

export function calculateArraysSum(arrays) {
  if (!Array.isArray(arrays)) {
    throw new Error('Invalid input. Expected an array.');
  }

  if (arrays.length === 0) {
    return 0;
  }

  const array = arrays.flat();

  return array.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0,
  );
}

export function getLastIntervalCount(
  type: string,
  historyChartLastIntervalSeverityData: any,
) {
  switch (type) {
    case VulnStatus.New:
      return historyChartLastIntervalSeverityData
        ? calculateArraysSum(historyChartLastIntervalSeverityData[type])
        : 0;
    case VulnStatus.Reopened:
      return historyChartLastIntervalSeverityData
        ? calculateArraysSum(historyChartLastIntervalSeverityData[type])
        : 0;
    case VulnStatus.Fixed:
      return historyChartLastIntervalSeverityData
        ? calculateArraysSum(historyChartLastIntervalSeverityData[type])
        : 0;
    case VulnStatus.Ignored:
      return historyChartLastIntervalSeverityData
        ? calculateArraysSum(historyChartLastIntervalSeverityData[type])
        : 0;
    default:
      return 0;
  }
}

function getLastNodeCount(datasets) {
  let count = 0;
  if (datasets?.length) {
    // get last node count
    datasets.forEach((dataset) => {
      if (dataset?.length) {
        count += dataset[dataset?.length - 1];
      }
    });
  }
  return count.toString();
}

export function getVulnStatusSum(type: string, historicalData: any) {
  switch (type) {
    case VulnStatus.New:
      return historicalData
        ? getLastNodeCount(historicalData[VulnStatus.New])
        : '0';
    case VulnStatus.Reopened:
      return historicalData
        ? getLastNodeCount(historicalData[VulnStatus.Reopened])
        : '0';
    case VulnStatus.Fixed:
      return historicalData
        ? getLastNodeCount(historicalData[VulnStatus.Fixed])
        : '0';
    case VulnStatus.Ignored:
      return historicalData
        ? getLastNodeCount(historicalData[VulnStatus.Ignored])
        : '0';
    default:
      return '0';
  }
}
